/**
 * THE sole client owner of persisted authenticated-member appearance /
 * workspace-default preference state, its member-scoped fallback cache (Cache
 * B), and all `/profile/me` GET/PATCH for these groups.
 *
 * `useTheme` is a CONSUMER — it reads `themeMode` + `hasActiveMemberContext`
 * here and owns nothing persisted. See `_docs/architecture/theme-system.md` §2 / §11.
 *
 * Module-level singleton (house pattern — like `useChartDisplaySettings` /
 * `useZoomAdjustmentPreferences`). Not Pinia.
 */

import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import { authState } from '@/utils/auth';
import {
  defaultAppearanceSettings,
  deserializePreferences,
  readMemberAppearanceCache,
  serializeNewWorkspaceCanvasModePatch,
  serializeSavedCanvasDefaultPatch,
  serializeThemeModePatch,
  writeMemberAppearanceCache,
} from '@/utils/appearanceSettings';
import { normalizeThemeMode } from '@/utils/themePreference';
import {
  isValidSavedCanvasDefault,
  normalizeNewWorkspaceCanvasMode,
  normalizeWorkspaceDefaults,
} from '@/utils/workspaceNewCanvasDefault';
import { cloneWorkspaceValue } from '@/utils/cloneWorkspaceValue';

const state = reactive(defaultAppearanceSettings());

const loaded = ref(false);
const loading = ref(false);
const loadSource = ref(null); // 'server' | 'cache' | 'default' | null
const activeContextUserId = ref(null);

let loadPromise = null;
let loadGeneration = 0;
let saveTimer = null;
let pendingPatch = null;

const hasActiveMemberContext = computed(
  () =>
    authState.userId != null &&
    activeContextUserId.value === authState.userId &&
    loaded.value,
);

const themeMode = computed(() => state.themeMode);
const workspaceDefaults = computed(() => ({
  newWorkspaceCanvasMode: state.workspaceDefaults.newWorkspaceCanvasMode,
  savedCanvasDefault: state.workspaceDefaults.savedCanvasDefault
    ? cloneWorkspaceValue(state.workspaceDefaults.savedCanvasDefault)
    : null,
}));

function applyNormalized(model, source) {
  const normalizedDefaults = normalizeWorkspaceDefaults(model.workspaceDefaults);
  state.themeMode = normalizeThemeMode(model.themeMode);
  state.workspaceDefaults.newWorkspaceCanvasMode = normalizedDefaults.newWorkspaceCanvasMode;
  state.workspaceDefaults.savedCanvasDefault = normalizedDefaults.savedCanvasDefault;
  loadSource.value = source;
}

function snapshot() {
  return {
    themeMode: state.themeMode,
    workspaceDefaults: workspaceDefaults.value,
    loadSource: loadSource.value,
  };
}

export function resetActiveMemberState() {
  loadGeneration += 1; // abandon any in-flight load
  loadPromise = null;
  loaded.value = false;
  loading.value = false;
  loadSource.value = null;
  activeContextUserId.value = null;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  pendingPatch = null;
  const defaults = defaultAppearanceSettings();
  state.themeMode = defaults.themeMode;
  state.workspaceDefaults.newWorkspaceCanvasMode = defaults.workspaceDefaults.newWorkspaceCanvasMode;
  state.workspaceDefaults.savedCanvasDefault = null;
}

export async function ensureAppearanceSettingsLoaded({ force = false } = {}) {
  if (!force) {
    if (loadPromise) return loadPromise;
    // Already settled for the current auth context -> no new request.
    if (
      loaded.value &&
      ((authState.userId == null && activeContextUserId.value == null) ||
        activeContextUserId.value === authState.userId)
    ) {
      return snapshot();
    }
  }

  const gen = (loadGeneration += 1);
  loadPromise = (async () => {
    loading.value = true;
    try {
      if (!authState.isAuthenticated || authState.userId == null) {
        if (gen === loadGeneration) {
          applyNormalized(defaultAppearanceSettings(), 'default');
          activeContextUserId.value = null;
        }
        return snapshot();
      }

      const userId = authState.userId;
      try {
        const response = await axios.get('/api/v1/profile/me');
        if (gen !== loadGeneration) return snapshot();
        const model = deserializePreferences(response.data?.preferences);
        applyNormalized(model, 'server');
        writeMemberAppearanceCache(userId, model);
        activeContextUserId.value = userId;
      } catch {
        if (gen !== loadGeneration) return snapshot();
        const cached = readMemberAppearanceCache(userId);
        applyNormalized(cached ?? defaultAppearanceSettings(), cached ? 'cache' : 'default');
        activeContextUserId.value = userId;
      }
      return snapshot();
    } finally {
      if (gen === loadGeneration) {
        loading.value = false;
        loaded.value = true;
      }
    }
  })();

  try {
    return await loadPromise;
  } finally {
    if (gen === loadGeneration) loadPromise = null;
  }
}

function flushPatch() {
  const body = pendingPatch;
  pendingPatch = null;
  saveTimer = null;
  if (!body || !authState.isAuthenticated) return;
  axios.patch('/api/v1/profile/me', body).catch(() => {
    /* Cache B holds the change; a later successful load reconciles. */
  });
}

function schedulePatch(body) {
  // Merge successive group patches so a burst of setters issues one request.
  pendingPatch = pendingPatch
    ? {
        preferences: {
          ...pendingPatch.preferences,
          ...body.preferences,
          ...(pendingPatch.preferences.workspace_defaults || body.preferences.workspace_defaults
            ? {
                workspace_defaults: {
                  ...(pendingPatch.preferences.workspace_defaults || {}),
                  ...(body.preferences.workspace_defaults || {}),
                },
              }
            : {}),
        },
      }
    : body;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(flushPatch, 400);
}

function persistCacheB() {
  if (authState.userId == null) return;
  writeMemberAppearanceCache(authState.userId, {
    themeMode: state.themeMode,
    workspaceDefaults: state.workspaceDefaults,
  });
}

export function setThemeMode(mode) {
  const normalized = normalizeThemeMode(mode);
  if (state.themeMode === normalized) return;
  state.themeMode = normalized;
  persistCacheB();
  schedulePatch(serializeThemeModePatch(normalized));
}

export function setNewWorkspaceCanvasMode(mode) {
  const normalized = normalizeNewWorkspaceCanvasMode(mode);
  if (state.workspaceDefaults.newWorkspaceCanvasMode === normalized) return;
  state.workspaceDefaults.newWorkspaceCanvasMode = normalized;
  persistCacheB();
  schedulePatch(serializeNewWorkspaceCanvasModePatch(normalized));
}

export function setSavedCanvasDefault(bundle) {
  const next = isValidSavedCanvasDefault(bundle) ? cloneWorkspaceValue(bundle) : null;
  state.workspaceDefaults.savedCanvasDefault = next;
  // Clearing the saved default also drops the mode back to match-app-theme
  // (a `saved-default` mode with nothing saved would just fall back anyway).
  if (next == null && state.workspaceDefaults.newWorkspaceCanvasMode === 'saved-default') {
    state.workspaceDefaults.newWorkspaceCanvasMode = 'match-app-theme';
    schedulePatch(serializeNewWorkspaceCanvasModePatch('match-app-theme'));
  }
  persistCacheB();
  schedulePatch(serializeSavedCanvasDefaultPatch(next));
}

// Auth transitions — module scope so this is wired exactly once for the app.
watch(
  () => authState.userId,
  (userId) => {
    resetActiveMemberState();
    if (userId != null) {
      ensureAppearanceSettingsLoaded({ force: true });
    }
  },
);

export function useAppearanceSettings() {
  return {
    themeMode,
    workspaceDefaults,
    hasActiveMemberContext,
    loaded,
    loading,
    loadSource,
    ensureLoaded: ensureAppearanceSettingsLoaded,
    resetActiveMemberState,
    setThemeMode,
    setNewWorkspaceCanvasMode,
    setSavedCanvasDefault,
  };
}
